import type { SubjectKind, StoredSubjectKind } from './subject-navigation'

/**
 * The navigation scope is intentionally separate from domain ownership. A
 * record can be opened from Workspace or from a Subject without duplicating
 * that record in persistence.
 */
export type ApplicationScope =
    | { mode: 'WORKSPACE'; workspaceId?: string }
    | {
          mode: 'SUBJECT'
          workspaceId?: string
          subjectType: SubjectKind
          subjectId: string
          subjectDisplayName?: string
      }

export type SubjectRouteScope = Pick<Extract<ApplicationScope, { mode: 'SUBJECT' }>, 'subjectType' | 'subjectId'>

export function isStoredSubjectKind(value: string | undefined): value is StoredSubjectKind {
    return value === 'person' || value === 'company' || value === 'product'
}

export function storedSubjectKind(subjectType: string | undefined): StoredSubjectKind | undefined {
    const normalized = subjectType === 'group' ? 'person' : subjectType
    return isStoredSubjectKind(normalized) ? normalized : undefined
}

export function subjectScope(subjectType: string | undefined, subjectId: string | undefined): SubjectRouteScope | null {
    if (!subjectId || !subjectType) return null
    if (!['person', 'group', 'company', 'product'].includes(subjectType)) return null
    return { subjectType: subjectType as SubjectKind, subjectId }
}

/** Build a URL that remains inside the Subject shell. */
export function subjectFeaturePath(scope: SubjectRouteScope, feature: string, suffix = ''): string {
    const normalizedFeature = feature.replace(/^\/+|\/+$/g, '')
    const normalizedSuffix = suffix.replace(/^\/+/, '')
    return `/dashboard/subjects/${encodeURIComponent(scope.subjectType)}/${encodeURIComponent(scope.subjectId)}/${normalizedFeature}${normalizedSuffix ? `/${normalizedSuffix}` : ''}`
}

/**
 * Legacy/shared pages may still need a query context while their contextual
 * route is being introduced. The query is URL-addressable and survives tabs,
 * refreshes, and browser history; it is never an in-memory-only state.
 */
export function withSubjectQuery(path: string, scope: SubjectRouteScope | null): string {
    if (!scope) return path
    const separator = path.includes('?') ? '&' : '?'
    return `${path}${separator}subjectType=${encodeURIComponent(scope.subjectType)}&subjectId=${encodeURIComponent(scope.subjectId)}`
}

export function scopedOrWorkspacePath(
    scope: SubjectRouteScope | null,
    workspacePath: string,
    subjectFeature: string,
    subjectSuffix = ''
): string {
    return scope ? subjectFeaturePath(scope, subjectFeature, subjectSuffix) : workspacePath
}

export function subjectFoundationPath(scope: SubjectRouteScope, runId: string, suffix = ''): string {
    return subjectFeaturePath(scope, 'foundations', `${runId}${suffix ? `/${suffix.replace(/^\/+/, '')}` : ''}`)
}
